import { Injectable } from "@angular/core";
import {
  BrandBgEntity,
  BrandBgEntityRaw,
  BrandCaptionLowerThirdEntity,
  BrandCaptionLowerThirdEntityRaw,
  BrandLogoEntity,
  BrandLogoEntityRaw,
  BrandOverlayEntity,
  BrandOverlayEntityRaw,
  RoomAnyId,
  RoomEntity,
  RoomEntityRaw,
  RoomId,
  RoomLiveStreamStatusUpdateEventVO,
  RoomLiveStreamStatusUpdateEventVORaw
} from "@common/domain";
import { ApiService } from "@youm-front/api/data-access";
import { LocalConferenceStreamsListService } from "@youm-front/conference-stream/data-access";
import { YoumAPIFacade } from "@youm-front/core";
import {
  RoomJoinCmd,
  RoomStreamAudioUpdateCmd,
  RoomStreamRestartCmd
} from "@youm-front/room/domain";
import { defer, Observable, of } from "rxjs";
import { map, mapTo, mergeMap } from "rxjs/operators";
import {
  AdminUpdateRoomStreamReq,
  AdminUpdateRoomStreamsOrderReq,
  Empty
} from "youm-lib";
import { LocalCamMicConferenceStream } from "youm-lib/conference";
import {
  RoomBrandBgDeferredErrorDTO,
  RoomBrandBgDeleteEventDTO,
  RoomBrandBgSetEventDTO,
  RoomBrandCaptionLowerThirdDeferredErrorDTO,
  RoomBrandCaptionLowerThirdDeleteEventDTO,
  RoomBrandCaptionLowerThirdSetEventDTO,
  RoomBrandLogoDeferredErrorDTO,
  RoomBrandLogoDeleteEventDTO,
  RoomBrandLogoSetEventDTO,
  RoomBrandOverlayDeferredErrorDTO,
  RoomBrandOverlayDeleteEventDTO,
  RoomBrandOverlaySetEventDTO,
  RoomMixerErrorEventDTO,
  RoomMixerErrorEventJson,
  RoomMixerStartedEventDTO,
  RoomMixerStartedEventJson,
  RoomMixerStartEventDTO,
  RoomMixerStartEventJson,
  RoomMixerToRotateEventDTO,
  RoomMixerToRotateEventJson,
  RoomStageLayoutDeferredErrorEventDTO,
  RoomStageLayoutStreamsSetEventDTO,
  RoomStageLayoutStreamsSetEventDTORaw,
  RoomStreamAudioUpdateEventDTO,
  RoomStreamAudioUpdateEventDTORaw,
  RoomStreamAudioUpdateRequestDTO,
  RoomStreamAudioUpdateRequestDTORaw,
  RoomStreamDeleteEventDTO,
  RoomStreamDeleteEventDTORaw,
  RoomStreamDeleteRequestDTO,
  RoomStreamRestartEventDTO,
  RoomStreamRestartEventDTORaw,
  RoomStreamRestartRequestDTO,
  RoomStreamRestartRequestDTORaw,
  RoomStreamsListRequestDTO,
  RoomStreamsListRequestDTORaw,
  RoomStreamsListResponseDTO,
  RoomStreamsListResponseDTORaw,
  RoomStreamToPublishCreateRequestDTO,
  RoomStreamToPublishCreateRequestDTORaw,
  RoomStreamToPublishCreateResponseDTO,
  RoomStreamToPublishCreateResponseDTORaw,
  RoomStreamToSubscribeEventDTO,
  RoomStreamToSubscribeEventDTORaw,
  RoomStreamUpdateEventDTO,
  RoomStreamUpdateEventDTORaw
} from "../dto";
import { LocalCamMicStreamService } from "./local-cammic-stream.service";

@Injectable()
export class RoomService {
  constructor(
    private readonly apiService: ApiService,
    private readonly localCamMicStreamService: LocalCamMicStreamService,
    private readonly localConferenceStreamsListService: LocalConferenceStreamsListService,
    private readonly youmAPIFacade: YoumAPIFacade
  ) {}

  listenRoomStreamEvent() {
    return this.youmAPIFacade
      .listenRoomStreamEvent<RoomStreamToSubscribeEventDTORaw>()
      .pipe(map((_) => new RoomStreamToSubscribeEventDTO(_)));
  }

  listenRoomStreamDeleteEvent() {
    return this.youmAPIFacade
      .listenRoomStreamEndEvent<RoomStreamDeleteEventDTORaw>()
      .pipe(map((_) => new RoomStreamDeleteEventDTO(_)));
  }

  listenRoomStreamUpdateEvent() {
    return this.youmAPIFacade
      .listenRoomStreamUpdateEvent<RoomStreamUpdateEventDTORaw>()
      .pipe(map((_) => new RoomStreamUpdateEventDTO(_)));
  }

  listenRoomStreamRestartEvent() {
    return this.youmAPIFacade
      .listenRestartStreamEvent<RoomStreamRestartEventDTORaw>()
      .pipe(map((_) => new RoomStreamRestartEventDTO(_)));
  }

  listenRoomStreamAudioUpdateEvent() {
    return this.youmAPIFacade
      .listenUpdateAudioStreamEvent<RoomStreamAudioUpdateEventDTORaw>()
      .pipe(map((_) => new RoomStreamAudioUpdateEventDTO(_)));
  }

  listenAddDestinationEvent() {
    return this.youmAPIFacade.listenAddDestinationEvent();
  }

  listenUpdateDestinationEvent() {
    return this.youmAPIFacade.listenUpdateDestinationEvent();
  }

  listenDeleteDestinationEvent() {
    return this.youmAPIFacade.listenDeleteDestinationEvent();
  }

  listenErrorDestinationEvent() {
    return this.youmAPIFacade.listenErrorDestinationEvent();
  }

  listenLicenseLimitExceeded() {
    return this.youmAPIFacade.listenLicenseLimitExceeded();
  }

  listenRoomBrandCaptionLowerThirdSet() {
    return this.youmAPIFacade
      .listenRoomBrandCaptionLowerThirdSet<{
        roomId: RoomId;
        caption: BrandCaptionLowerThirdEntityRaw;
      }>()
      .pipe(
        map(
          (_) =>
            new RoomBrandCaptionLowerThirdSetEventDTO({
              roomId: _.roomId,
              caption: new BrandCaptionLowerThirdEntity(_.caption)
            })
        )
      );
  }

  listenRoomBrandCaptionLowerThirdDelete() {
    return this.youmAPIFacade
      .listenRoomBrandCaptionLowerThirdDelete<{
        roomId: RoomId;
      }>()
      .pipe(
        map(
          (_) =>
            new RoomBrandCaptionLowerThirdDeleteEventDTO({
              roomId: _.roomId
            })
        )
      );
  }

  listenRoomBrandCaptionLowerThirdDeferredError() {
    return this.youmAPIFacade
      .listenRoomBrandCaptionLowerThirdDeferredError()
      .pipe(map((_) => new RoomBrandCaptionLowerThirdDeferredErrorDTO(_)));
  }

  listenRoomBrandBgSet() {
    return this.youmAPIFacade
      .listenRoomBrandBgSet<{
        roomId: RoomId;
        brandBg: BrandBgEntityRaw;
      }>()
      .pipe(
        map(
          (_) =>
            new RoomBrandBgSetEventDTO({
              roomId: _.roomId,
              brandBg: new BrandBgEntity(_.brandBg)
            })
        )
      );
  }

  listenRoomBrandBgDelete() {
    return this.youmAPIFacade
      .listenRoomBrandBgDelete<{
        roomId: RoomId;
      }>()
      .pipe(
        map(
          (_) =>
            new RoomBrandBgDeleteEventDTO({
              roomId: _.roomId
            })
        )
      );
  }

  listenRoomBrandBgDeferredError() {
    return this.youmAPIFacade
      .listenRoomBrandBgDeferredError()
      .pipe(map((_) => new RoomBrandBgDeferredErrorDTO(_)));
  }

  listenRoomBrandLogoSet() {
    return this.youmAPIFacade
      .listenRoomBrandLogoSet<{
        roomId: RoomId;
        brandLogo: BrandLogoEntityRaw;
      }>()
      .pipe(
        map(
          (_) =>
            new RoomBrandLogoSetEventDTO({
              roomId: _.roomId,
              brandLogo: new BrandLogoEntity(_.brandLogo)
            })
        )
      );
  }

  listenRoomBrandLogoDelete() {
    return this.youmAPIFacade
      .listenRoomBrandLogoDelete<{
        roomId: RoomId;
      }>()
      .pipe(
        map(
          (_) =>
            new RoomBrandLogoDeleteEventDTO({
              roomId: _.roomId
            })
        )
      );
  }

  listenRoomBrandLogoDeferredError() {
    return this.youmAPIFacade
      .listenRoomBrandLogoDeferredError()
      .pipe(map((_) => new RoomBrandLogoDeferredErrorDTO(_)));
  }

  listenRoomBrandOverlaySet() {
    return this.youmAPIFacade
      .listenRoomBrandOverlaySet<{
        roomId: RoomId;
        brandOverlay: BrandOverlayEntityRaw;
      }>()
      .pipe(
        map(
          (_) =>
            new RoomBrandOverlaySetEventDTO({
              roomId: _.roomId,
              brandOverlay: new BrandOverlayEntity(_.brandOverlay)
            })
        )
      );
  }

  listenRoomBrandOverlayDelete() {
    return this.youmAPIFacade
      .listenRoomBrandOverlayDelete<{
        roomId: RoomId;
      }>()
      .pipe(
        map(
          (_) =>
            new RoomBrandOverlayDeleteEventDTO({
              roomId: _.roomId
            })
        )
      );
  }

  listenRoomBrandOverlayDeferredError() {
    return this.youmAPIFacade
      .listenRoomBrandOverlayDeferredError()
      .pipe(map((_) => new RoomBrandOverlayDeferredErrorDTO(_)));
  }

  listenRoomMixerError() {
    return this.youmAPIFacade
      .listenRoomMixerError<RoomMixerErrorEventJson>()
      .pipe(map((_) => new RoomMixerErrorEventDTO(_)));
  }

  listenRoomMixerStart() {
    return this.youmAPIFacade
      .listenRoomMixerStart<RoomMixerStartEventJson>()
      .pipe(map((_) => new RoomMixerStartEventDTO(_)));
  }

  listenRoomMixerStarted() {
    return this.youmAPIFacade
      .listenRoomMixerStarted<RoomMixerStartedEventJson>()
      .pipe(map((_) => new RoomMixerStartedEventDTO(_)));
  }

  listenRoomMixerToRotate() {
    return this.youmAPIFacade
      .listenRoomMixerToRotate<RoomMixerToRotateEventJson>()
      .pipe(map((_) => new RoomMixerToRotateEventDTO(_)));
  }

  listenRoomLiveStreamStatusUpdate() {
    return this.youmAPIFacade
      .listenRoomLiveStreamStatusUpdate<RoomLiveStreamStatusUpdateEventVORaw>()
      .pipe(map((_) => new RoomLiveStreamStatusUpdateEventVO(_)));
  }

  listenRoomStageLayoutDeferredError() {
    return this.youmAPIFacade
      .listenRoomStageLayoutDeferredError()
      .pipe(map((_) => new RoomStageLayoutDeferredErrorEventDTO(_)));
  }

  listenRoomStageLayoutStreamsSet() {
    return this.youmAPIFacade
      .listenRoomStageLayoutStreamsSet<RoomStageLayoutStreamsSetEventDTORaw>()
      .pipe(map((_) => new RoomStageLayoutStreamsSetEventDTO(_)));
  }

  listRoomStreams(
    roomStreamsListRequestDTO: RoomStreamsListRequestDTO
  ): Observable<RoomStreamsListResponseDTO> {
    return this.youmAPIFacade
      .listRoomStreams<
        RoomStreamsListResponseDTORaw,
        RoomStreamsListRequestDTORaw
      >(roomStreamsListRequestDTO.data)
      .pipe(map((_) => new RoomStreamsListResponseDTO(_)));
  }

  roomJoin(roomJoinCmd: RoomJoinCmd): Observable<void> {
    return this.youmAPIFacade
      .roomJoin(roomJoinCmd.value)
      .pipe(map(() => void 0));
  }

  roomStreamAudioUpdate(
    roomStreamAudioUpdateCmd: RoomStreamAudioUpdateCmd
  ): Observable<void> {
    const { roomId, streamId, isEnabled } = roomStreamAudioUpdateCmd.value;

    return this.roomStreamLocalAudioUpdate(roomStreamAudioUpdateCmd).pipe(
      mergeMap((_) =>
        _
          ? of(void 0)
          : this.youmAPIFacade.updateAudioStream<RoomStreamAudioUpdateRequestDTORaw>(
              new RoomStreamAudioUpdateRequestDTO({
                roomId,
                streamId,
                isEnabled
              }).data
            )
      )
    );
  }

  roomStreamDelete(roomStreamDeleteCmd: RoomStreamDeleteRequestDTO) {
    return this.youmAPIFacade.roomStreamEnd(roomStreamDeleteCmd.data);
  }

  roomStreamLocalAudioUpdate(
    roomStreamAudioUpdateCmd: RoomStreamAudioUpdateCmd
  ): Observable<boolean> {
    const { streamId, isEnabled } = roomStreamAudioUpdateCmd.value;

    const stream = this.localConferenceStreamsListService.streams.find(
      (stream) => stream.streamId === streamId
    );

    if (!stream) {
      return of(false);
    }

    if (stream instanceof LocalCamMicConferenceStream) {
      return isEnabled
        ? defer(() => this.localCamMicStreamService.audioEnable()).pipe(
            map(() => true)
          )
        : defer(() => this.localCamMicStreamService.audioDisable()).pipe(
            map(() => true)
          );
    }

    if (isEnabled) {
      return defer(() => stream.audioEnable()).pipe(map(() => true));
    }

    return defer(() => stream.audioDisable()).pipe(map(() => true));
  }

  roomStreamLocalRestart(roomStreamRestartCmd: RoomStreamRestartCmd): boolean {
    const { streamId } = roomStreamRestartCmd.value;

    const stream = this.localConferenceStreamsListService.streams.find(
      (stream) => stream.streamId === streamId
    );

    if (stream && "restart" in stream) {
      stream.restart();

      return true;
    }

    return false;
  }

  roomStreamRestart(
    roomStreamRestartCmd: RoomStreamRestartCmd
  ): Observable<void> {
    if (this.roomStreamLocalRestart(roomStreamRestartCmd)) {
      return of(void 0);
    }

    const { roomId, streamId } = roomStreamRestartCmd.value;

    return this.youmAPIFacade
      .restartStream<RoomStreamRestartRequestDTORaw>(
        new RoomStreamRestartRequestDTO({ roomId, streamId }).data
      )
      .pipe(mapTo(void 0));
  }

  /**
   * @deprecated Should not be used.
   */
  adminUpdateRoomStream(
    data: AdminUpdateRoomStreamReq
  ): Observable<null | Empty> {
    return this.youmAPIFacade.adminUpdateRoomStream(data);
  }

  /**
   * @deprecated Should not be used.
   */
  adminUpdateRoomStreamsOrder(
    data: AdminUpdateRoomStreamsOrderReq
  ): Observable<Empty> {
    return this.youmAPIFacade.adminUpdateRoomStreamsOrder(data);
  }

  roomGet(roomId: RoomAnyId): Observable<RoomEntity> {
    return this.apiService
      .get<{ room: RoomEntityRaw }>(`rooms/${roomId}`)
      .pipe(map((_) => new RoomEntity(_.room)));
  }

  roomStreamToPublishCreate(
    roomStreamToPublishCreateRequestDTO: RoomStreamToPublishCreateRequestDTO
  ): Observable<RoomStreamToPublishCreateResponseDTO> {
    return this.youmAPIFacade
      .roomStream<
        RoomStreamToPublishCreateResponseDTORaw,
        RoomStreamToPublishCreateRequestDTORaw
      >(roomStreamToPublishCreateRequestDTO.data)
      .pipe(map((_) => new RoomStreamToPublishCreateResponseDTO(_)));
  }

  startLiveStream(roomId: RoomId): Observable<RoomEntity> {
    return this.apiService
      .post<{ room: RoomEntityRaw }>(`rooms/${roomId}/live`, {})
      .pipe(map((_) => new RoomEntity(_.room)));
  }

  stopLiveStream(roomId: RoomId) {
    return this.apiService.delete<void>(`rooms/${roomId}/live`);
  }
}
